import { z } from "zod";

const trimmed = (label: string, min = 1, max = 120) =>
  z
    .string({ required_error: `${label} is required.` })
    .trim()
    .min(min, `${label} is required.`)
    .max(max, `${label} must be ${max} characters or fewer.`);

export const emailSchema = z
  .string({ required_error: "Email is required." })
  .trim()
  .email("Enter a valid email address.")
  .max(254, "Email must be 254 characters or fewer.");

export const contactSchema = z.object({
  name: trimmed("Name", 2, 80),
  email: emailSchema,
  subject: trimmed("Subject", 3, 120),
  message: z
    .string({ required_error: "Message is required." })
    .trim()
    .min(20, "Message should be at least 20 characters.")
    .max(4000, "Message must be 4000 characters or fewer."),
  website: z.string().max(0).default(""),
  turnstileToken: z.string().trim().max(2048).optional().or(z.literal("")),
});

export const donationAmountSchema = z
  .number({ invalid_type_error: "Enter a valid amount." })
  .int("Amount must be a whole number of cents.")
  .min(100, "Minimum donation is $1.00.")
  .max(1_000_000, "Maximum online donation is $10,000.00.");

export const donationSchema = z.object({
  donorName: trimmed("Full name", 2, 100),
  donorEmail: emailSchema,
  amount: donationAmountSchema,
  provider: z.enum(["stripe", "paypal"], {
    required_error: "Choose a payment method.",
  }),
  allowPublicRecognition: z.boolean().default(false),
  publicDisplayName: z
    .string()
    .trim()
    .max(100, "Public display name must be 100 characters or fewer.")
    .optional()
    .or(z.literal("")),
  website: z.string().max(0).default(""),
  turnstileToken: z.string().trim().max(2048).optional().or(z.literal("")),
});

export const donationLookupSchema = z.object({
  provider: z.enum(["stripe", "paypal"]),
  sessionId: z
    .string()
    .trim()
    .min(3)
    .max(255)
    .optional(),
  orderId: z
    .string()
    .trim()
    .min(3)
    .max(255)
    .optional(),
});

export const paypalCaptureSchema = z.object({
  orderId: z.string().trim().min(3).max(255),
});

export type ContactInput = z.infer<typeof contactSchema>;
export type DonationInput = z.infer<typeof donationSchema>;
export type DonationLookupInput = z.infer<typeof donationLookupSchema>;
export type PaypalCaptureInput = z.infer<typeof paypalCaptureSchema>;
